import store from '@/store/store'
import { queryClient } from '@network/queryClient'
import { logger } from '@logger'
import { notify, gameToasts } from '@/core/toast'
import { renderValue } from '@utils/numberUtils'
import { getGlobalParams } from '@core/graphQL/globalParams'
import { GAMES } from '@core/appearance/games'
import { shareWin } from '@core/chat/chatClient'
import { emitPlaceBet } from '@sockets/socketEmitters'
import { GAME_STATUS } from '@sockets/socketEvents'
import {
  BET_STATE,
  TOGGLE,
  BET_SUBTYPE,
  BET_RESULT,
  PANELS,
  PANEL_IDS,
  PLACE_BET_ERROR_ACTION,
} from '@constants/bettingConstants'
import { classifyPlaceBetError } from '@core/betting/betErrors'
import { isSubmissionDue } from '@core/betting/betSubmissionWindow'
import { submitClientSeedForCurrentRound } from '@core/provablyFair/clientSeed'
import {
  queueFreebet,
  markFreebetSubmitted,
  clearFreebetPending,
  acceptFreebet,
  markFreebetRunning,
  settleFreebetCashout,
  markFreebetCrashed,
  resetFreebet,
} from '@slices/bettingSlice'

/**
 * The single writer for FREEBET bet lifecycle — a parallel of the Classic
 * orchestrator, sharing the same PLACE_BET/CASH_OUT socket path and the same
 * BET_STATE machine, but staking a freebet (`subType: FREEBET`) instead of cash.
 * Framework-agnostic (plain module) so it is the one place that mutates freebet
 * lifecycle, and it never races a second writer.
 *
 * Submission is DEFERRED exactly as in Classic: pressing Freebet only queues the
 * bet (BET_PLACED, cancelable), and `freebetTryEmit` withholds PLACE_BET until the
 * submission window is due. Once emitted the panel is BET_SUBMITTED and final —
 * no cancel, no rollback.
 *
 * Two freebet-specific realities drive the design:
 *  - The BE debits 0 for a freebet, so there is NO balance guard here.
 *  - NO server event echoes the freebetId and there is NO freebet-list push, so
 *    the FE must re-fetch the list after a placement/settlement to see a slot
 *    consumed. That refetch is triggered here (invalidate ['freebets']).
 */
const MODULE = 'Freebet Betting'

// FIFO of freebet bets emitted this round but not yet matched to a
// PLACE_BET_RESPONSE. Because Classic and Freebets are mutually exclusive while
// busy (tab lock), only ONE orchestrator ever has entries here — which is how
// socketListeners routes place-replies to the right owner.
let pendingEmits = []

const state = () => store.getState()
const dispatch = (action) => store.dispatch(action)
const panels = () => state().betting.freebet.panels
const round = () => state().events.round
// Which layout is live: dual (two freebet panels) or single. Reconcile needs it.
const dualMode = () => Boolean(state().layout?.duelBetMode?.freebets)

// The id sent to the BE as `freebetId`. The BE forwards it verbatim to the ROOT
// service; the working reference FE sends `playerFreebetId`, so we match that.
// One place to flip to `id` if ROOT ever wants the grant id instead.
const freebetWireId = (fb) => fb?.playerFreebetId ?? fb?.id ?? null

// ── correlation (identical strategy to Classic) ──────────────────────────────
const resolvePanel = (data) => {
  if (data?.panel === PANELS.ONE || data?.panel === PANELS.TWO) return data.panel
  const roundId = data?.roundId
  const idx = pendingEmits.findIndex((e) => e.roundId == null || e.roundId === roundId)
  const entry = idx === -1 ? pendingEmits.shift() : pendingEmits.splice(idx, 1)[0]
  return entry?.panel ?? null
}

const panelByTxn = (transactionId) => {
  if (!transactionId) return null
  const p = panels()
  return PANEL_IDS.find((id) => p[id]?.transactionId === transactionId) ?? null
}

// Whether we're awaiting any PLACE_BET_RESPONSE. socketListeners uses this to
// route a place-reply to the freebet vs the Classic orchestrator (only one can
// be non-empty at a time).
export const hasPendingEmit = () => pendingEmits.length > 0

// Re-fetch the server freebet list so a consumed slot disappears (the BE pushes
// no such update). Only the mounted useFreebets query refetches.
const refreshFreebets = () => queryClient.invalidateQueries({ queryKey: ['freebets'] })

// Win pop-up, formatted outside React (mirrors Classic notifyWin). betAmount is
// the freebet's fixed amount, captured from the frozen snapshot.
const notifyWin = (panel, payout, payoutMultiplier) => {
  const amount = renderValue({ value: payout, conventions: state().conventions })
  const multiplier =
    payoutMultiplier != null ? `${Number(payoutMultiplier).toFixed(2)}x` : undefined

  const chatEnabled = Boolean(state().configurations?.featureToggles?.chat)
  const { gameCode } = getGlobalParams()
  const betAmount = Number(panels()[panel]?.placedFreebet?.rawAmount) || 0

  const onShare = chatEnabled
    ? () =>
        shareWin({
          gameName: GAMES[gameCode]?.gameName,
          cashoutMultiplier: payoutMultiplier,
          cashoutAmount: payout,
          betAmount,
        })
    : undefined

  gameToasts.cashout({ amount, multiplier, onShare })
}

// A panel is free to take a new bet only when nothing is live on it: idle, or
// already settled/crashed this round.
const canAcceptNewBet = (betState) =>
  betState === BET_STATE.IDLE ||
  betState === BET_STATE.BET_CASHED_OUT ||
  betState === BET_STATE.CRASHED

// ── player intent ────────────────────────────────────────────────────────────
// Both entry points re-read LIVE store state rather than trusting what the card
// rendered, because the scheduler can fire between a render and the click it
// produced (mirrors Classic).

/**
 * Store a pending freebet bet from a player press, freezing the snapshot the card
 * renders for the rest of the lifecycle.
 */
export const placeFreebetBet = (panel, freebet) => {
  const p = panels()[panel]
  if (!p || !freebet || !canAcceptNewBet(p.betState)) return
  dispatch(queueFreebet({ panel, freebet }))

  // Provably fair v2: the player's contribution to this round's outcome. A freebet
  // round is fair on the same terms as a cash one, so the same seed goes out.
  // Fire-and-forget — never awaited, cannot throw, and nothing below reads it.
  submitClientSeedForCurrentRound()

  freebetTryEmit(panel)
}

/**
 * Discard a pending freebet bet, freeing the freebet for re-selection. A submitted
 * bet is final, so this becomes a no-op.
 */
export const cancelPendingFreebet = (panel) => {
  const p = panels()[panel]
  if (!p) return
  if (p.betState === BET_STATE.BET_PLACED) {
    dispatch(resetFreebet({ panel, dualMode: dualMode() }))
    return
  }
  // Nothing pending. If a bet is live, the press raced the submission — the bet
  // is final, so there is deliberately nothing to undo.
  if (!canAcceptNewBet(p.betState)) {
    logger.info(MODULE, 'Cancel ignored — freebet already submitted', panel)
  }
}

// ── emit ─────────────────────────────────────────────────────────────────────
// Sends a pending freebet bet IF the round is accepting AND the submission window
// is due. Safe anytime; no-ops otherwise, so the bet stays pending (and
// cancelable) until the scheduler fires. Stakes the frozen snapshot captured at
// queue time, never the (possibly refetched) live list.
export const freebetTryEmit = (panel) => {
  const p = panels()[panel]
  if (!p) return
  if (round().status !== GAME_STATUS.ACCEPT_BET) return
  if (!isSubmissionDue()) return // hold the bet — not yet time to send
  if (p.betState !== BET_STATE.BET_PLACED) return // nothing pending, or already sent

  const fb = p.placedFreebet
  const amount = Number(fb?.rawAmount) || 0
  if (!fb || amount <= 0) {
    logger.error(MODULE, 'Cannot place freebet: missing/invalid freebet', fb)
    dispatch(resetFreebet({ panel, dualMode: dualMode() }))
    return
  }

  const { currency, gameCode } = getGlobalParams()
  if (!currency) {
    logger.error(MODULE, 'Cannot place freebet: currency missing')
    dispatch(resetFreebet({ panel, dualMode: dualMode() }))
    return
  }

  const target = Number(p.autoCashout.value)
  const cashOutAt = p.autoCashout.status === TOGGLE.ENABLED && target > 1 ? target : 0

  // Second, guarded attempt — this line always runs inside ACCEPT_BET, so it is
  // the one that lands for a bet queued during the previous round. The per-round
  // guard inside collapses the pair to a single request in the normal case.
  submitClientSeedForCurrentRound()

  pendingEmits.push({ panel, roundId: round().roundId })
  dispatch(markFreebetSubmitted({ panel }))

  const ok = emitPlaceBet({
    betAmount: amount,
    currency,
    cashOutAt,
    panel,
    subType: BET_SUBTYPE.FREEBET,
    source: fb.source ?? null, // required by the BE for a freebet
    freebetId: freebetWireId(fb),
    sourceId: fb.packageId ?? null, // optional origin id (e.g. bought package)
    jackpotRatio: fb.jackpotRatio ?? null,
    gameCode,
  })

  if (!ok) {
    // The emit never left the client (socket down) — nothing was staked, so hand
    // the bet back to the player as pending.
    pendingEmits = pendingEmits.filter((e) => e.panel !== panel)
    dispatch(queueFreebet({ panel, freebet: fb }))
  }
}

// ── round-phase hooks (called from socketListeners) ──────────────────────────
export const onScheduled = () => {
  pendingEmits = []
  const dm = dualMode()
  PANEL_IDS.forEach((panel) => {
    const s = panels()[panel].betState
    // resetFreebet also re-points the freed panel against the (now refetched) list
    if (s === BET_STATE.CRASHED || s === BET_STATE.BET_CASHED_OUT) {
      dispatch(resetFreebet({ panel, dualMode: dm }))
    }
  })
}

// No ACCEPT_BET hook: freebets have no auto-bet loop to re-arm, and sending a
// queued bet is the scheduler's job (it calls `freebetTryEmit` when due).

export const onRunning = () => {
  PANEL_IDS.forEach((panel) => {
    if (panels()[panel].betState === BET_STATE.BET_ACCEPTED) dispatch(markFreebetRunning({ panel }))
  })
}

export const onEnded = () => {
  PANEL_IDS.forEach((panel) => {
    const p = panels()[panel]
    if (p.betState === BET_STATE.BET_RUNNING) dispatch(markFreebetCrashed({ panel }))
    // Submitted but never acked — the round is over and we hold no transactionId,
    // so free the panel instead of stranding it (mirrors Classic).
    if (p.betState === BET_STATE.BET_SUBMITTED) {
      logger.warn(MODULE, 'Round ended with no place-bet reply — releasing panel', panel)
      dispatch(resetFreebet({ panel, dualMode: dualMode() }))
    }
    if (p.pending) dispatch(clearFreebetPending({ panel }))
  })
}

// ── command replies (routed from socketListeners) ────────────────────────────
export const onPlaceBetResponse = (msg) => {
  const panel = resolvePanel(msg?.data)
  if (!panel) return // not a freebet emit we're tracking

  if (msg?.success) {
    const { transactionId, roundId } = msg.data || {}
    dispatch(acceptFreebet({ panel, transactionId, roundId }))
    refreshFreebets() // a slot was consumed — the BE won't push it
    return
  }

  const action = classifyPlaceBetError(msg?.code)
  if (action === PLACE_BET_ERROR_ACTION.RETRY) {
    // Round wasn't open — nothing was staked, so the bet returns to pending and
    // is submitted in the next round's window.
    dispatch(queueFreebet({ panel, freebet: panels()[panel].placedFreebet }))
    logger.info(MODULE, 'Freebet not accepted, held for next round', msg?.code)
  } else {
    dispatch(resetFreebet({ panel, dualMode: dualMode() }))
    notify('error', { title: 'Freebet failed', sub: msg?.message })
  }
}

export const onCashoutResponse = (msg) => {
  const panel = panelByTxn(msg?.data?.transactionId)
  if (!panel) return
  if (msg?.success) {
    dispatch(settleFreebetCashout({ panel, cashoutAmount: msg.data?.payout ?? null }))
    notifyWin(panel, msg.data?.payout, msg.data?.payoutMultiplier)
  } else {
    dispatch(clearFreebetPending({ panel }))
    notify('error', { title: 'Cashout failed', sub: msg?.message })
  }
}

export const onBetSettled = (msg) => {
  // Authoritative final record + safety net (same role as Classic onBetSettled).
  const panel = panelByTxn(msg?.data?.transactionId)
  if (!panel) return
  const current = panels()[panel].betState
  if (current === BET_STATE.IDLE) return

  const data = msg?.data || {}
  const payout = Number(data.payout) || 0
  const won = data.result === BET_RESULT.CASHED_OUT || data.result === BET_RESULT.WON || payout > 0

  if (won) {
    if (current !== BET_STATE.BET_CASHED_OUT) {
      dispatch(settleFreebetCashout({ panel, cashoutAmount: payout }))
      notifyWin(panel, payout, data.payoutMultiplier)
    }
  } else if (current !== BET_STATE.CRASHED) {
    dispatch(markFreebetCrashed({ panel }))
  }
  if (panels()[panel].pending) dispatch(clearFreebetPending({ panel }))
  refreshFreebets() // settlement may change remaining slots / win totals
}
